export interface IFieldError {
    /** The request path of the field, such as `design_config.min_tsrf_pct` or `monthly_consumption_kwh.3`. */
    path: string;
    message: string;
}

/** The es-ds `state` of an input for a request path: `false` shows the service's error, `null` shows none. */
export const fieldState = (fieldErrors: Record<string, string>, path: string) => (fieldErrors[path] ? false : null);

/** The id of the message under a marked field, which the field's control names in `aria-describedby`. */
export const errorId = (path: string): string => `${path.replace(/[^\w-]+/g, '-')}-error`;

/** A failed call to the system-design service. `status` 0 means the service could not be reached. */
export class SystemDesignApiError extends Error {
    constructor(
        public readonly status: number,
        message: string,
        public readonly fieldErrors: IFieldError[] = [],
    ) {
        super(message);
        this.name = 'SystemDesignApiError';
    }
}

const UNREACHABLE =
    "Can't reach the system-design service. Check that it's running (`poetry run poe local` in service/).";

/** The service's error envelope (`message`, and a 422's `detail`) as an error a page can show in place. */
export function toApiError(status: number, body: unknown): SystemDesignApiError {
    if (status === 0) {
        return new SystemDesignApiError(0, UNREACHABLE);
    }
    const { message, detail } = (typeof body === 'object' && body !== null ? body : {}) as {
        message?: unknown;
        detail?: unknown;
    };
    const fieldErrors = Array.isArray(detail) ? detail.flatMap(toFieldError) : [];
    const text = typeof message === 'string' && message ? message : `The service answered ${status}.`;
    return new SystemDesignApiError(status, text, fieldErrors);
}

export function asApiError(error: unknown): SystemDesignApiError {
    if (error instanceof SystemDesignApiError) return error;
    return new SystemDesignApiError(0, error instanceof Error ? error.message : String(error));
}

function toFieldError(problem: unknown): IFieldError[] {
    const { loc, msg } = (typeof problem === 'object' && problem !== null ? problem : {}) as {
        loc?: unknown;
        msg?: unknown;
    };
    if (!Array.isArray(loc) || typeof msg !== 'string') return [];
    return [{ path: loc.filter((part) => part !== 'body').join('.'), message: readable(msg) }];
}

/** Pydantic prefixes a validator's own message with "Value error, ": the message alone reads better next to a field. */
function readable(message: string): string {
    const text = message.replace(/^Value error, /, '');
    return text.charAt(0).toUpperCase() + text.slice(1);
}
